import {
  COLOR_MIRA_BAR,
  COLOR_MIRA_BAR_DARK,
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
} from "@/utils/colors";
import { MiraBar, MiraViewContext, formatPercent } from "@/utils/MiraDataQualityView";
import { MiraCaseUnit, unitText } from "@/utils/MiraCaseUnits";

/**
 * A breakdown over many named parts as sorted horizontal bars: the Geräte category mix, and the
 * projects per branch and per project type on Geschäftsentwicklung.
 *
 * Market research (2026-10-01): past about six parts a ring stops being readable, and a sorted bar chart
 * is the standard. One colour for every bar, because the labels carry identity and a colour per part
 * would only repeat them.
 */

/** How many parts are shown before the rest are folded into one "Weitere (N)" bar. */
export const MIRA_SORTED_BAR_ROWS = 7;

/** The row key of the "Weitere (N)" fold: a sentinel, so it never collides with a real part id. */
export const MIRA_SORTED_BAR_OTHERS_KEY = "__others__";

/**
 * The row key of the single folded "Unbekannte Kategorie" / "Unbekannter Standort" bar, which stands
 * for every part id the caller could not name. A sentinel, so it is one stable key however many
 * unnamed parts it replaces.
 */
export const MIRA_SORTED_BAR_UNKNOWN_KEY = "__unknown__";

/**
 * The bars for a published breakdown, largest first: each bar scaled to the largest, its value the
 * part's share of the whole, or its count where the parts do not add up to a whole.
 *
 * Past {@link MIRA_SORTED_BAR_ROWS} the rest fold into one "Weitere (N)" bar, except a single leftover,
 * which keeps its name: "Weitere (1)" would say less than the name it replaces.
 *
 * Every part whose id the caller cannot name is folded, before ranking, into one `unknownLabel` bar
 * whose value is their summed count, so a mix never shows several identical "Unbekannte …" rows (a
 * group master naming its members' category ids, or a deleted branch, is the usual cause). That one
 * folded bar then ranks and may itself fall into "Weitere" like any other. Named parts that share a
 * display name (two members' own "Kondenstrockner" categories on a group master) merge into one bar.
 *
 * @param {Record<string, number>} breakdown - the published counts by id.
 * @param {Record<string, string>} names - the display name of each id the caller knows.
 * @param {string} unknownLabel - the label of an id it does not.
 * @param {MiraViewContext} ctx - translation, number formatting and theme.
 * @param {"share" | "count"} valueAs - "count" when one item can fall into several parts (Prozess's
 *   out-of-order steps: one project can have several), so a share of the parts' sum means nothing.
 * @param {MiraCaseUnit | null} countUnit - for a share, what the parts count (Projekte, Gerätetage):
 *   each row then names its count beside its share (Track #2610072 S1). A count row shows it already.
 * @param {number | null} shareBase - with "count": the whole the parts overlap within (Schadenursachen: the
 *   projects with a cause), so each row reads its share of it, percentage first, its count beside
 *   (#2610112 X2). The folded "Weitere" row stays a count: its parts overlap, so no share of it is true.
 * @returns {MiraBar[]} the bars, or none when the breakdown is empty.
 */
export function buildSortedBars(
  breakdown: Record<string, number> | null | undefined,
  names: Record<string, string>,
  unknownLabel: string,
  ctx: MiraViewContext,
  valueAs: "share" | "count" = "share",
  countUnit: MiraCaseUnit | null = null,
  shareBase: number | null = null
): MiraBar[] {
  const counted = Object.entries(breakdown ?? {}).filter(([, count]) => count > 0);
  const total = counted.reduce((sum, [, count]) => sum + count, 0);
  if (!total) return [];

  const hasName = (id: string): boolean => names[id] !== undefined && names[id] !== "";

  // Fold every unnamed part into one bar before ranking, so the list never carries several identical
  // "Unbekannte …" rows; a named part keeps its own id as its key.
  // Parts that share a display name are one row too: on a group master two members can each have their
  // own "Kondenstrockner" category (different ids, the same kind of device). The row keeps the smallest
  // of its ids as its key, so the key is stable whatever order the breakdown arrives in.
  const byLabel = new Map<string, { key: string; label: string; count: number }>();
  counted
    .filter(([id]) => hasName(id))
    .forEach(([id, count]) => {
      const label = names[id];
      const row = byLabel.get(label);
      if (row) {
        row.count += count;
        if (id < row.key) row.key = id;
      } else {
        byLabel.set(label, { key: id, label, count });
      }
    });
  const ranked: Array<{ key: string; label: string; count: number }> = Array.from(byLabel.values());
  const unknownCount = counted.filter(([id]) => !hasName(id)).reduce((sum, [, count]) => sum + count, 0);
  if (unknownCount > 0) {
    ranked.push({ key: MIRA_SORTED_BAR_UNKNOWN_KEY, label: unknownLabel, count: unknownCount });
  }
  ranked.sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));

  const shown = ranked.length > MIRA_SORTED_BAR_ROWS + 1 ? ranked.slice(0, MIRA_SORTED_BAR_ROWS) : ranked;
  const rest = ranked.slice(shown.length);
  const rows = shown.map((row) => ({ key: row.key, label: row.label, count: row.count }));
  if (rest.length) {
    rows.push({
      key: MIRA_SORTED_BAR_OTHERS_KEY,
      label: ctx.t("mira.format.others", [ctx.formatNumber(rest.length)]),
      count: rest.reduce((sum, row) => sum + row.count, 0),
    });
  }

  const largest = Math.max(...rows.map((row) => row.count));
  const ofBase = valueAs === "count" && shareBase !== null && shareBase > 0 ? shareBase : null;
  return rows.map((row) => {
    // The folded "Weitere" row reads like every other row (Taimoor, 2026-10-29): its share is the sum of the
    // causes it folds, on the same rule as the rest (a project counts under each of its causes).
    const sharedOfBase = ofBase !== null;
    return {
      key: row.key,
      label: row.label,
      fraction: row.count / largest,
      valueText: sharedOfBase
        ? formatPercent(row.count / (ofBase as number), ctx)
        : valueAs === "count"
        ? ctx.formatNumber(row.count)
        : formatPercent(row.count / total, ctx),
      countText: sharedOfBase
        ? unitText(countUnit ?? "projects", row.count, ctx)
        : valueAs === "share" && countUnit !== null
        ? unitText(countUnit, row.count, ctx)
        : null,
      color: ctx.dark ? COLOR_MIRA_BAR_DARK : COLOR_MIRA_BAR,
      trackColor: ctx.dark ? COLOR_MIRA_BAR_TRACK_DARK : COLOR_MIRA_BAR_TRACK,
      deltaText: null,
      deltaDirection: "flat" as const,
      showDelta: false,
    };
  });
}
